import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // Import navigation hook
import {
  collection,
  addDoc,
  getFirestore,
  doc,
  getDoc,
} from "firebase/firestore";

import "../CSS/App.css";

import { getAuth, onAuthStateChanged } from "firebase/auth";
import { FaLocationArrow } from "react-icons/fa";
import AddressInput from "./AddressInput";
import { getCurrentPosition } from "../utils/proximity";
import { reverseGeocode } from "../utils/geocode";

const AddItem = () => {
  const [Title, setTitle] = useState("");
  const [Address, setAddress] = useState("");
  const [coords, setCoords] = useState(null);
  const [category, setCategory] = useState("");
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState("");
  const [locateAccuracy, setLocateAccuracy] = useState(null);
  const navigate = useNavigate(); // Initialize navigation
  const db = getFirestore();
  const [user, setUser] = useState(null);
  const auth = getAuth();
  const [userCategories, setUserCategories] = useState([]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const addressRef = collection(db, "users", user.uid, "addresses");
      await addDoc(addressRef, {
        Title,
        Address,
        lat: coords ? coords.lat : null,
        lng: coords ? coords.lng : null,
        category,
        notes,
        isFavorite: false,
        navigationCount: 0,
        lastNavigatedAt: null,
      });

      navigate("/addresses");
    } catch (error) {
      console.error("Error adding address:", error);
    }
  };

  const handleCancel = () => {
    navigate("/addresses"); // Back to the address list
  };

  // Get the current user state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is signed in
        setUser(user);
        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists() && userDoc.data().categories) {
          setUserCategories(userDoc.data().categories);
        } else {
          // Existing users predating the categories feature have no
          // categories field on their doc yet - fall back to the same
          // defaults ManageCategories seeds new users with.
          setUserCategories(["Home", "Work"]);
        }
      } else {
        // No user is signed in
        navigate("/login");
      }
    });
    // Cleanup the listener when the component unmounts
    return () => unsubscribe();
  }, [navigate, auth, db]);

  const handleAddressSelect = (address, selectedCoords) => {
    setAddress(address);
    setCoords(selectedCoords || null);
  };

  // Fill the address (and its coordinates) from the device's current location,
  // reverse-geocoded to a street address - same flow as the event pickers.
  const handleLocate = async () => {
    setLocating(true);
    setLocateError("");
    try {
      const pos = await getCurrentPosition({ highAccuracy: true });
      if (!pos) {
        throw new Error(
          "Couldn't get your location. Check location permission and try again.",
        );
      }
      const address = await reverseGeocode(pos);
      setAddress(address);
      setCoords(pos);
      setLocateAccuracy(typeof pos.accuracy === "number" ? pos.accuracy : null);
    } catch (err) {
      console.error("Locate me failed:", err);
      setLocateError(err.message || "Couldn't detect your location.");
    } finally {
      setLocating(false);
    }
  };

  return (
    <div
      className="container"
      style={{
        width: "80%",
        maxWidth: "400px",
        margin: "auto",
        paddingTop: "calc(20px + env(safe-area-inset-top, 0px))",
      }}
    >
      <form>
        <h2>Add a New Address</h2>
        <div style={{ marginBottom: "10px" }}>
          <input
            type="text"
            id="title"
            value={Title}
            placeholder="Enter a title"
            onChange={(e) => setTitle(e.target.value)}
            style={{
              width: "90%",
              padding: "10px 15px",
              borderRadius: "20px",
              border: "1px solid var(--border-color, #ccc)",
            }}
          />
        </div>
        <div style={{ marginBottom: "10px" }}>
          <AddressInput
            id="address"
            onAddressSelect={handleAddressSelect}
            placeholder="Enter a location"
            value={Address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>
        <div style={{ marginBottom: "10px" }}>
          <button
            type="button"
            onClick={handleLocate}
            disabled={locating}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              width: "90%",
              boxSizing: "border-box",
              padding: "10px 15px",
              borderRadius: "20px",
              border: "1px dashed var(--border-color, #aaa)",
              backgroundColor: "transparent",
              color: "var(--text-primary, #1a1a1a)",
              cursor: locating ? "default" : "pointer",
              fontSize: "14px",
            }}
          >
            <FaLocationArrow size={14} color="#007bff" />
            {locating
              ? "Detecting your location..."
              : "Use my current location"}
          </button>
          <p
            style={{
              fontSize: "11px",
              color: "var(--text-secondary, #999)",
              margin: "6px 2px 0",
              lineHeight: 1.3,
            }}
          >
            {locateAccuracy != null
              ? `Approximate location (±${Math.round(locateAccuracy)} m) - please double-check the address.`
              : "Approximate - please double-check the address."}
          </p>
          {locateError && (
            <p style={{ color: "red", fontSize: "13px", marginTop: "6px" }}>
              {locateError}
            </p>
          )}
        </div>
        <div style={{ marginBottom: "10px" }}>
          <select
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={{
              width: "50%",
              padding: "10px",
              borderRadius: "20px",
              border: "1px solid var(--border-color, #ccc)",
            }}
          >
            <option value="">No Category</option>
            {userCategories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        {showNotes ? (
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (e.g. gate code, parking instructions)"
            rows={3}
            style={{
              width: "90%",
              padding: "10px",
              marginBottom: "10px",
              borderRadius: "20px",
              border: "1px solid var(--border-color, #ccc)",
              paddingLeft: "15px",
              fontFamily: "inherit",
              resize: "vertical",
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setShowNotes(true)}
            style={{
              background: "none",
              border: "none",
              color: "#007bff",
              cursor: "pointer",
              padding: 0,
              marginBottom: "15px",
              display: "block",
            }}
          >
            + Add notes
          </button>
        )}
        <div>
          <button
            type="button"
            onClick={handleAdd}
            style={{
              padding: "10px 30px",
              border: "none",
              cursor: "pointer",
              borderRadius: "5px",
              backgroundColor: "#4CAF50",
              color: "white",
              marginRight: "20px",
            }}
          >
            Add
          </button>
          <button
            type="button"
            onClick={handleCancel}
            style={{
              backgroundColor: "gray",
              color: "white",
              padding: "10px 15px",
              border: "none",
              cursor: "pointer",
              borderRadius: "5px",
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddItem;
